import { type CanvasElementId } from './canvas-element-id';
import { type WrapperRefs } from './props';
import { type WithSlots } from './slots';

export type FormFieldInfoType = 'summary' | 'form' | 'field' | 'action-button' | 'commit';
export type FieldInfoType = 'search' | 'filter' | 'parameter' | 'confirm' | FormFieldInfoType;

export type WrapperRefId = string;

export const SEARCH_AND_FILTERS_ELEMENT_SLOT_ID = 'searchAndFilters';
export type WithElementSearchAndFilters = WithSlots<typeof SEARCH_AND_FILTERS_ELEMENT_SLOT_ID>;

/**
 * An array of RefIds.
 * This is an array so the ids can be fallbacked to name.
 * At least one ref id must be provided.
 * The first one is the most important.
 */
export type WrapperRefIds<Id extends WrapperRefId = string> = [Id, ...Id[]];

interface WrapperItemHelper<Type extends FieldInfoType, Id extends WrapperRefId, Value = string> {
  type: Type;
  getRefIds: (value?: Value, id?: Value) => WrapperRefIds<Id>;
  isRefId: (refId: string) => refId is WrapperRefId;
  getValue: (refId: string | undefined) => string | undefined;
}

// Wrapper model refs (reference ids)
const wrapperItemTypeField: FormFieldInfoType = 'field';
const REFID_FIELD_PREFIX = `${wrapperItemTypeField}-`;
export type WrapperFieldRefId = `${typeof REFID_FIELD_PREFIX}${string}`;
export const WrapperField = createWrapperRefHelper<'field', WrapperFieldRefId>(
  wrapperItemTypeField,
  REFID_FIELD_PREFIX,
  true,
);

// Form Group - nested forms (reference form ids)
const wrapperItemTypeForm: FormFieldInfoType = 'form';
const REFID_FORM_PREFIX = `${wrapperItemTypeForm}-`;
export type WrapperFormRefId = `${typeof REFID_FORM_PREFIX}${string}`;
export const WrapperForm = createWrapperRefHelper<'form', WrapperFormRefId>(
  wrapperItemTypeForm,
  REFID_FORM_PREFIX,
  true,
);

// Wizard - summary views (reference details views ids)
const wrapperItemTypeSummary: FormFieldInfoType = 'summary';
const REFID_SUMMARY_PREFIX = `${wrapperItemTypeSummary}-`;
export type WrapperSummaryRefId = `${typeof REFID_SUMMARY_PREFIX}${string}`;
export const WrapperSummary = createWrapperRefHelper<'summary', WrapperSummaryRefId>(
  wrapperItemTypeSummary,
  REFID_SUMMARY_PREFIX,
  true,
);

const wrapperItemTypeActionButton: FormFieldInfoType = 'action-button';
const REFID_ACTION_BUTTON_PREFIX = `${wrapperItemTypeActionButton}-`;
export type WrapperActionButtonRefId = `${typeof REFID_ACTION_BUTTON_PREFIX}${string}`;
export const WrapperActionButton = createWrapperRefHelper<
  'action-button',
  WrapperActionButtonRefId
>(wrapperItemTypeActionButton, REFID_ACTION_BUTTON_PREFIX, true);

// Search & Filters - search
const FIELD_INFO_TYPE_SEARCH: FieldInfoType = 'search';
type WrapperSearchRefId = typeof FIELD_INFO_TYPE_SEARCH;
export const WrapperSearch = createWrapperRefHelper<'search', WrapperSearchRefId, undefined>(
  FIELD_INFO_TYPE_SEARCH,
  undefined,
  false,
);

// Search & Filters - filter
const FIELD_INFO_TYPE_FILTER: FieldInfoType = 'filter';
type WrapperFilterRefId = `${typeof FIELD_INFO_TYPE_FILTER}${string}`;
export const WrapperFilter = createWrapperRefHelper<'filter', WrapperFilterRefId>(
  FIELD_INFO_TYPE_FILTER,
  FIELD_INFO_TYPE_FILTER,
  true,
);

// API Call - Parameter field
const FIELD_INFO_TYPE_PARAMETER: FieldInfoType = 'parameter';
const REFID_PARAMETER_PREFIX = `${FIELD_INFO_TYPE_PARAMETER}_`;
export type WrapperParameterRefId = `${typeof REFID_PARAMETER_PREFIX}${string}`;
export const WrapperParameter = createWrapperRefHelper<'parameter', WrapperParameterRefId>(
  FIELD_INFO_TYPE_PARAMETER,
  REFID_PARAMETER_PREFIX,
  true,
);

// API Call - Confirm button
const FIELD_INFO_TYPE_CONFIRM: FieldInfoType = 'confirm';
export type WrapperConfirmButtonRefId = typeof FIELD_INFO_TYPE_CONFIRM;
export const WrapperConfirmButton = createWrapperRefHelper<
  'confirm',
  WrapperConfirmButtonRefId,
  undefined
>(FIELD_INFO_TYPE_CONFIRM, undefined, false);

export function findByRefIds<Id extends WrapperRefId>(
  refs: WrapperRefs | undefined,
  refIds: WrapperRefIds<Id>,
): CanvasElementId | undefined {
  if (!refs) {
    return undefined;
  }

  for (const refId of refIds) {
    if (refId in refs) {
      return refs[refId];
    }
  }

  return undefined;
}

// Dialog submit button
export const FIELD_INFO_TYPE_COMMIT: FieldInfoType = 'commit';
export type WrapperCommitButtonRefId = typeof FIELD_INFO_TYPE_COMMIT;
export const WrapperCommitButton = createWrapperRefHelper<
  'commit',
  WrapperCommitButtonRefId,
  undefined
>(FIELD_INFO_TYPE_COMMIT, undefined, false);

export function getWrapperItems(refs: WrapperRefs): WrapperItem[] {
  const helpers = [
    WrapperField,
    WrapperActionButton,
    WrapperSearch,
    WrapperFilter,
    WrapperParameter,
    WrapperConfirmButton,
  ];
  const items: WrapperItem[] = [];

  for (const [refId, elementId] of Object.entries(refs)) {
    for (const helper of helpers) {
      if (helper.isRefId(refId)) {
        items.push({
          refId,
          elementId,
          type: helper.type,
          value: helper.getValue(refId),
        });
      }
    }
  }

  return items;
}

export function getWizardSummaryFormsIds(refs: WrapperRefs): {
  formId: CanvasElementId;
  detailsId: CanvasElementId;
}[] {
  return Object.entries(refs)
    .filter(([ref]) => WrapperSummary.isRefId(ref))
    .map(([ref, detailsId]) => ({ formId: WrapperSummary.getValue(ref) ?? '', detailsId }));
}

/** get the main form of a form group like */
export function getFormGroupLikeWrapperMainFormId(refs: WrapperRefs): {
  mainFormId: CanvasElementId | undefined;
  datasetId: string | undefined;
} {
  // main form ref: { `form-${dataSourceId}`: formId }
  const [refId, mainFormId] =
    Object.entries(refs).find(
      ([ref]) =>
        WrapperForm.isRefId(ref) && (WrapperForm.getValue(ref) ?? '---').split('-').length === 1,
    ) ?? [];
  const datasetId = WrapperForm.getValue(refId);
  return { mainFormId, datasetId };
}

/** get all forms of a form group like */
export function getFormGroupLikeWrapper(refs: WrapperRefs): {
  formId: CanvasElementId;
  datasetId: string;
  keyColumnId: string | undefined;
  index: number | undefined;
}[] {
  // form ref: { `form-${dataSourceId}-${keyColumnId?}-${index?}`: formId }
  return Object.entries(refs)
    .filter(([ref]) => WrapperForm.isRefId(ref))
    .map(([ref, formId]) => {
      const fields = (WrapperForm.getValue(ref) ?? '').split('-');
      return {
        formId: formId as CanvasElementId,
        datasetId: fields[0],
        keyColumnId: fields[1] !== '' ? fields[1] : undefined,
        index: fields[2] !== undefined ? parseInt(fields[2]) : undefined,
      };
    });
}

export function setWrapperRefMutably(
  refs: WrapperRefs,
  refIds: WrapperRefIds,
  value: CanvasElementId,
): void {
  refIds.forEach((refId, index) => {
    if (index === 0) {
      // Use first refId to store the value
      refs[refId] = value;
    } else {
      // Remove all other refIds (backward compatibility)
      delete refs[refId];
    }
  });
}

export function deleteWrapperRefsMutably(refs: WrapperRefs, refIdsToDelete: WrapperRefIds): void {
  for (const refId of refIdsToDelete) {
    delete refs[refId];
  }
}

function createWrapperRefHelper<
  Type extends FieldInfoType,
  Id extends WrapperRefId,
  Value = string,
>(
  type: Type,
  refPrefix: Value extends undefined ? undefined : Id, // TODO: Make sure this enforces the provided RefId type
  hasValue: Value extends undefined ? false : true,
): WrapperItemHelper<Type, Id, Value> {
  function getRefIds(value?: Value, id?: Value): WrapperRefIds<Id> {
    const refIds: Id[] = [];
    if (!hasValue) {
      return [type as string as Id];
    }
    if (id !== undefined) {
      refIds.push(`${refPrefix}${id}` as Id);
    }
    if (value !== undefined) {
      refIds.push(`${refPrefix}${value}` as Id);
    }
    return refIds as WrapperRefIds<Id>;
  }

  function isRefId(refId: string): refId is Id {
    if (!refId) {
      return false;
    } else if (!hasValue) {
      return refId === type;
    } else {
      if (!refPrefix) {
        throw new Error('Wrapper ref having a value should have a prefix specified');
      }
      return refId.startsWith(refPrefix);
    }
  }

  function getValue(fieldRefId: string | undefined): string | undefined {
    if (!hasValue) {
      return undefined;
    } else {
      if (!refPrefix) {
        throw new Error('Wrapper ref having a value should have a prefix specified');
      }
      return fieldRefId?.substring(refPrefix.length);
    }
  }

  return { type, getRefIds, isRefId, getValue };
}

export interface WrapperItem {
  refId: string;
  elementId: CanvasElementId;
  type: FieldInfoType;
  value: string | undefined;
}
